import {
  RewardsOverlay,
  RewardsOverlayBackgroundColors,
  RewardsOverlayThemeOption,
  toRewardsOverlayBackgroundColor,
} from "@playlive/overlay-core/overlays/rewards";
import { deepSignal } from "deepsignal";
import { useMemo } from "preact/hooks";
import Picker from "@/components/input/picker.input.jsx";
import YesNoButtonInput from "@/components/input/yes-no-button.input.jsx";
import CustomizeSection from "@/components/overlays/shared/customize-section.jsx";
import LinkSetup from "@/components/overlays/social/link-setup/link-setup.jsx";
import HorizontalCustomizeWrapper from "@/components/overlays/wrapper/horizonal-customize-wrapper.jsx";
import ScaledOverlayWrapper from "@/components/overlays/wrapper/scaled-overlay-wrapper.jsx";
import YesNoButtonInputWrapper from "@/components/overlays/wrapper/yes-no-button-input-wrapper.jsx";
import ColorPicker from "@/components/pickers/color-picker/color.picker.jsx";
import ColorPickerLabel from "@/components/pickers/color-picker/color-picker-label.jsx";
import { rewards } from "@/data/example/index.js";
import useCommon from "@/hooks/useCommon.js";
import { isYes } from "@/util/common.js";
import { CHARITY } from "@/util/const.js";

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  rewardsTheme: RewardsOverlayThemeOption.CLASSIC,
  rewardsBackgroundColor: "default-red",
  highlightFeaturedRewards: "yes",
  showQuantityClaimed: "yes",
  showAllRewards: "yes",
  displayTimer: "0",
});

export default function RewardsOverlayPage() {
  const useBetaOverlays = isYes(import.meta.env.VITE_BETA_OVERLAYS || "no");
  const { overlayInfo, overlayURL, overlayDemoURL, setConfigState } = useCommon({
    config,
    forceTiltify: true,
    urlBase: useBetaOverlays ? "overlays/rewards" : "rewards",
  });

  const timerOptions = useMemo(
    () => [
      {
        value: "5",
        name: "Five minutes",
      },
      {
        value: "10",
        name: "Ten minutes",
      },
      {
        value: "15",
        name: "Fifteen minutes",
      },
      {
        value: "0",
        name: "Always",
      },
    ],
    [],
  );

  const themeOptions = [
    {
      name: "Slanted",
      value: RewardsOverlayThemeOption.SLANTED,
    },
    {
      name: "Rounded",
      value: RewardsOverlayThemeOption.ROUNDED,
    },
    {
      name: "Classic",
      value: RewardsOverlayThemeOption.CLASSIC,
    },
  ];

  const rewardOptions = [
    {
      name: "All rewards",
      value: "yes",
    },
    {
      name: "Highlighted rewards",
      value: "no",
    },
  ];

  function filterColors(color) {
    return RewardsOverlayBackgroundColors.find((c) => c === color);
  }

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col xl:flex-row overflow-x-hidden">
        <div className="example_overlay overflow-x-hidden w-full min-h-72">
          <ScaledOverlayWrapper
            className="min-w-142.5"
            allowScaling={false}
            defaultScale={"scale-100"}
          >
            <RewardsOverlay
              goalAmount={1000}
              currentAmount={750}
              showAllRewards={isYes(config.showAllRewards)}
              showQuantityClaimed={isYes(config.showQuantityClaimed)}
              highlightFeaturedRewards={isYes(config.highlightFeaturedRewards)}
              backgroundColor={toRewardsOverlayBackgroundColor(config.rewardsBackgroundColor)}
              demoMode
              theme={config.rewardsTheme}
              rewards={rewards}
              timer={config.rotationTimer}
            />
          </ScaledOverlayWrapper>
        </div>

        <HorizontalCustomizeWrapper className="flex flex-col mb-0!">
          <CustomizeSection title="Appearance" className="grow">
            <ColorPickerLabel color={config.rewardsBackgroundColor} />

            <ColorPicker
              fieldName="rewardsBackgroundColor"
              currentColor={config.rewardsBackgroundColor}
              filterFunction={filterColors}
              onChange={setConfigState}
            />

            <Picker
              items={themeOptions}
              fieldName="rewardsTheme"
              onChange={setConfigState}
              currentValue={config.rewardsTheme}
              labelText="Theme"
              className="mb-2"
            />

            <Picker
              items={timerOptions}
              fieldName="displayTimer"
              onChange={setConfigState}
              currentValue={config.displayTimer}
              labelText="Visibility"
              // description="Set amount of time to rotate in and out or to always be visible
              //   on stream"
              description="Set how often your rewards automatically appear and disappear, or keep them always visible."
            />

            <Picker
              items={rewardOptions}
              fieldName="showAllRewards"
              onChange={setConfigState}
              currentValue={config.showAllRewards}
              labelText="Display Rewards"
              description="Display all or only highlighted rewards"
              className="mb-2"
            />

            <YesNoButtonInputWrapper>
              <YesNoButtonInput
                mode="checkbox"
                className="md:mr-4 mb-2 md:mb-0 flex-1"
                onChange={setConfigState}
                currentValue={config.highlightFeaturedRewards}
                name="highlightFeaturedRewards"
                label="Feature highlighted rewards"
              />

              <YesNoButtonInput
                className="flex-1"
                mode="checkbox"
                onChange={setConfigState}
                currentValue={config.showQuantityClaimed}
                name="showQuantityClaimed"
                label="Display quantity claimed"
              />
            </YesNoButtonInputWrapper>
          </CustomizeSection>

          {/* Charity Section Placeholder */}
        </HorizontalCustomizeWrapper>
      </div>

      <LinkSetup
        allowTeamCampaignSelection={true}
        constrainWidth={false}
        charityType={CHARITY.TILTIFY}
        obsDimensions={overlayInfo.obsDimensions}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
        finalStepSlotPosition="middle"
        finalStepContent={
          <div className="mt-4">
            <a href={overlayDemoURL} rel="noopener noreferrer" className="link" target="_blank">
              View in demo mode
            </a>
          </div>
        }
      />
    </div>
  );
}
